import { postOrderPortal } from "./submitOrderToSheet";

/**
 * Customer portal (OTP login, order history, loyalty) — same endpoint as order submit.
 * Body is JSON with `action` field; Apps Script routes by action.
 */

export async function requestCustomerOtp(email) {
  return postOrderPortal({
    action: "requestOtp",
    email: String(email || "").trim().toLowerCase(),
  });
}

export async function verifyCustomerOtp(email, otp) {
  return postOrderPortal({
    action: "verifyOtp",
    email: String(email || "").trim().toLowerCase(),
    otp: String(otp || "").trim(),
  });
}

/** OTP to any inbox — no prior order required. Use before checkout; session proves email ownership. */
export async function requestCheckoutOtp(email) {
  return postOrderPortal({
    action: "requestCheckoutOtp",
    email: String(email || "").trim().toLowerCase(),
  });
}

export async function verifyCheckoutOtp(email, otp) {
  return postOrderPortal({
    action: "verifyCheckoutOtp",
    email: String(email || "").trim().toLowerCase(),
    otp: String(otp || "").trim(),
  });
}

export async function getLoyaltyStatus(email, sessionToken) {
  return postOrderPortal({
    action: "loyaltyStatus",
    email: String(email || "").trim().toLowerCase(),
    sessionToken: String(sessionToken || "").trim(),
  });
}

export async function getOrderHistory(email, sessionToken) {
  return postOrderPortal({
    action: "orderHistory",
    email: String(email || "").trim().toLowerCase(),
    sessionToken: String(sessionToken || "").trim(),
  });
}

export async function adminListOrders({ username, password }) {
  return postOrderPortal({
    action: "adminListOrders",
    adminUsername: String(username || "").trim(),
    adminPassword: String(password || ""),
  });
}

export async function adminUpdateOrderTracking({
  username,
  password,
  orderId,
  awb,
  shiprocketOrderId,
  shipmentId,
  labelUrl,
  trackingUrl,
  trackingStatus,
  trackingDetails,
}) {
  return postOrderPortal({
    action: "adminUpdateOrderTracking",
    adminUsername: String(username || "").trim(),
    adminPassword: String(password || ""),
    orderId: String(orderId || "").trim(),
    awb: String(awb || "").trim(),
    shiprocketOrderId: String(shiprocketOrderId || "").trim(),
    shipmentId: String(shipmentId || "").trim(),
    labelUrl: String(labelUrl || "").trim(),
    trackingUrl: String(trackingUrl || "").trim(),
    trackingStatus: String(trackingStatus || "").trim(),
    trackingDetails: trackingDetails || null,
  });
}

/** Past delivery details from Orders sheet for this email (last 100 rows). */
export async function getDeliveryHints(email, sessionToken) {
  return postOrderPortal({
    action: "deliveryHints",
    email: String(email || "").trim().toLowerCase(),
    sessionToken: String(sessionToken || "").trim(),
  });
}

/** Validate coupon against "Coupon codes" sheet (dates + active). Sends cart lines so server matches checkout totals. */
export async function validateCouponCode({
  couponCode,
  email,
  itemsDetail,
  loyaltyVoucherOrder,
  loyaltySessionToken,
}) {
  return postOrderPortal({
    action: "validateCoupon",
    couponCode: String(couponCode || "").trim(),
    email: String(email || "").trim().toLowerCase(),
    itemsDetail: itemsDetail || [],
    loyaltyVoucherOrder: !!loyaltyVoucherOrder,
    loyaltySessionToken: String(loyaltySessionToken || "").trim(),
  });
}

/** About / contact form — public; appends ContactMessages sheet + store email. */
export async function submitContactMessage({ name, email, message, source }) {
  return postOrderPortal({
    action: "submitContactMessage",
    name: String(name || "").trim(),
    email: String(email || "").trim().toLowerCase(),
    message: String(message || "").trim(),
    source: String(source || "about").trim().slice(0, 40),
  });
}

/** Approved reviews for a product (public; no session). */
export async function getProductReviews(productId) {
  return postOrderPortal({
    action: "productReviews",
    productId: String(productId || "").trim(),
  });
}

/** Latest approved reviews site-wide (homepage testimonials). */
export async function getSiteReviews(limit = 12) {
  return postOrderPortal({
    action: "siteReviews",
    limit: Number(limit) || 12,
  });
}

/** Logged-in customer submits a review (saved as approved; set sheet Status to pending to hide). */
export async function submitProductReview({
  email,
  sessionToken,
  productId,
  orderId,
  rating,
  comment,
}) {
  return postOrderPortal({
    action: "submitProductReview",
    email: String(email || "").trim().toLowerCase(),
    sessionToken: String(sessionToken || "").trim(),
    productId: String(productId || "").trim(),
    orderId: String(orderId || "").trim(),
    rating: Number(rating),
    comment: String(comment || "").trim(),
  });
}
